import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Customer,
  CreateCustomerDto,
  UpdateCustomerDto,
  CustomerType,
  CustomerStatus,
  PreferredContact,
  formatCustomerType,
  formatCustomerStatus,
  getPreferredContactLabel,
} from '../api/customers';
import TabbedFormActions, { isNonEmptyString } from './ui/TabbedFormActions';
import AmountInput from './ui/AmountInput';

interface CustomerFormProps {
  customer?: Customer;
  onSubmit: (data: CreateCustomerDto | UpdateCustomerDto) => Promise<void>;
  onCancel: () => void;
}

const schema = yup.object().shape({
  name: yup.string().required('Name is required'),
  code: yup.string().required('Code is required'),
  type: yup.string().oneOf(Object.values(CustomerType)).required('Type is required'),
  status: yup.string().oneOf(Object.values(CustomerStatus)).optional(),
  firstName: yup.string().optional(),
  lastName: yup.string().optional(),
  contactPerson: yup.string().optional(),
  email: yup.string().email('Invalid email format').optional(),
  phone: yup.string().optional(),
  alternativePhone: yup.string().optional(),
  whatsapp: yup.string().optional(),
  preferredContact: yup.string().oneOf(Object.values(PreferredContact)).optional(),
  address: yup.string().optional(),
  city: yup.string().optional(),
  province: yup.string().optional(),
  postalCode: yup.string().optional(),
  country: yup.string().optional(),
  website: yup.string().url('Invalid URL format').optional(),
  dateOfBirth: yup.string().optional(),
  idNumber: yup.string().optional(),
  taxNumber: yup.string().optional(),
  registrationNumber: yup.string().optional(),
  creditLimit: yup.number().min(0, 'Credit limit must be positive').optional(),
  currentBalance: yup.number().optional(),
  emergencyContactName: yup.string().optional(),
  emergencyContactPhone: yup.string().optional(),
  description: yup.string().optional(),
});

type FormData = yup.InferType<typeof schema>;

export default function CustomerForm({ customer, onSubmit, onCancel }: CustomerFormProps) {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    control,
  } = useForm<FormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      name: '',
      code: '',
      type: CustomerType.INDIVIDUAL,
      status: CustomerStatus.ACTIVE,
      preferredContact: PreferredContact.EMAIL,
      country: 'South Africa',
      currentBalance: 0,
    },
  });

  const watchedType = watch('type');

  useEffect(() => {
    if (customer) {
      reset({
        ...customer,
        dateOfBirth: customer.dateOfBirth ? new Date(customer.dateOfBirth).toISOString().split('T')[0] : '',
      });
    }
  }, [customer, reset]);

  const handleFormSubmit = async (data: FormData) => {
    try {
      setLoading(true);
      
      // Convert dateOfBirth string back to Date if provided
      const formattedData = {
        ...data,
        dateOfBirth: data.dateOfBirth ? new Date(data.dateOfBirth) : undefined,
      };
      
      await onSubmit(formattedData as any);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateCode = () => {
    const prefix = watchedType === CustomerType.INDIVIDUAL ? 'CUST' : 'BUS';
    const year = new Date().getFullYear().toString().slice(-2);
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    setValue('code', `${prefix}${year}${random}`);
  };

  const tabs = [
    { id: 'basic', name: 'Basic Info' },
    { id: 'contact', name: 'Contact' },
    { id: 'address', name: 'Address' },
    { id: 'business', name: 'Business' },
    { id: 'financial', name: 'Financial' },
    { id: 'additional', name: 'Additional' },
  ];

  const watchedName = watch('name');
  const watchedCode = watch('code');
  const canSubmit = isNonEmptyString(watchedName) && isNonEmptyString(watchedCode);
  const selectedTab = Math.max(0, tabs.findIndex((t) => t.id === activeTab));

  return (
    <div>
      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-cyan-500 text-cyan-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-gray-300'
              }`}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
        {/* Basic Info Tab */}
        {activeTab === 'basic' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Name *
                </label>
                <input
                  type="text"
                  {...register('name')}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                  placeholder="Customer or business name"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              {/* Code */}
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Code *
                </label>
                <div className="mt-1 flex rounded-md shadow-sm">
                  <input
                    type="text"
                    {...register('code')}
                    className="flex-1 block w-full border-gray-300 rounded-l-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    placeholder="CUST2024001"
                  />
                  <button
                    type="button"
                    onClick={generateCode}
                    className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 rounded-r-md bg-slate-50/80 text-slate-500 text-sm hover:bg-gray-100"
                  >
                    Generate
                  </button>
                </div>
                {errors.code && (
                  <p className="mt-1 text-sm text-red-600">{errors.code.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Type *
                </label>
                <select
                  {...register('type')}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                >
                  {Object.values(CustomerType).map((type) => (
                    <option key={type} value={type}>
                      {formatCustomerType(type)}
                    </option>
                  ))}
                </select>
                {errors.type && (
                  <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>
                )}
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Status
                </label>
                <select
                  {...register('status')}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                >
                  {Object.values(CustomerStatus).map((status) => (
                    <option key={status} value={status}>
                      {formatCustomerStatus(status)}
                    </option>
                  ))}
                </select>
                {errors.status && (
                  <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>
                )}
              </div>
            </div>

            {/* Individual Fields */}
            {watchedType === CustomerType.INDIVIDUAL && (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-slate-700">
                    First Name
                  </label>
                  <input
                    type="text"
                    {...register('firstName')}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700">
                    Last Name
                  </label>
                  <input
                    type="text"
                    {...register('lastName')}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
                  )}
                </div>
              </div>
            )}

            {/* Business Fields */}
            {watchedType !== CustomerType.INDIVIDUAL && (
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Contact Person
                </label>
                <input
                  type="text"
                  {...register('contactPerson')}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                  placeholder="Primary contact person"
                />
                {errors.contactPerson && (
                  <p className="mt-1 text-sm text-red-600">{errors.contactPerson.message}</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Contact Tab */}
        {activeTab === 'contact' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Email
                </label>
                <input
                  type="email"
                  {...register('email')}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                  placeholder="email@example.com"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              {/* Preferred Contact */}
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Preferred Contact Method
                </label>
                <select
                  {...register('preferredContact')}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                >
                  {Object.values(PreferredContact).map((contact) => (
                    <option key={contact} value={contact}>
                      {getPreferredContactLabel(contact)}
                    </option>
                  ))}
                </select>
                {errors.preferredContact && (
                  <p className="mt-1 text-sm text-red-600">{errors.preferredContact.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Primary Phone
                </label>
                <input
                  type="tel"
                  {...register('phone')}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                  placeholder="+27 12 345 6789"
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                )}
              </div>

              {/* Alternative Phone */}
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Alternative Phone
                </label>
                <input
                  type="tel"
                  {...register('alternativePhone')}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                  placeholder="+27 12 345 6789"
                />
                {errors.alternativePhone && (
                  <p className="mt-1 text-sm text-red-600">{errors.alternativePhone.message}</p>
                )}
              </div>

              {/* WhatsApp */}
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  WhatsApp
                </label>
                <input
                  type="tel"
                  {...register('whatsapp')}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                  placeholder="+27 12 345 6789"
                />
                {errors.whatsapp && (
                  <p className="mt-1 text-sm text-red-600">{errors.whatsapp.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              {/* Emergency Contact Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Emergency Contact Name
                </label>
                <input
                  type="text"
                  {...register('emergencyContactName')}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                />
                {errors.emergencyContactName && (
                  <p className="mt-1 text-sm text-red-600">{errors.emergencyContactName.message}</p>
                )}
              </div>

              {/* Emergency Contact Phone */}
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Emergency Contact Phone
                </label>
                <input
                  type="tel"
                  {...register('emergencyContactPhone')}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                />
                {errors.emergencyContactPhone && (
                  <p className="mt-1 text-sm text-red-600">{errors.emergencyContactPhone.message}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Address Tab */}
        {activeTab === 'address' && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Address
              </label>
              <textarea
                {...register('address')}
                rows={3}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                placeholder="Street address, building number, etc."
              />
              {errors.address && (
                <p className="mt-1 text-sm text-red-600">{errors.address.message}</p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {/* City */}
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  City
                </label>
                <input
                  type="text"
                  {...register('city')}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                />
                {errors.city && (
                  <p className="mt-1 text-sm text-red-600">{errors.city.message}</p>
                )}
              </div>

              {/* Province */}
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Province
                </label>
                <input
                  type="text"
                  {...register('province')}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                />
                {errors.province && (
                  <p className="mt-1 text-sm text-red-600">{errors.province.message}</p>
                )}
              </div>

              {/* Postal Code */}
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Postal Code
                </label>
                <input
                  type="text"
                  {...register('postalCode')}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                />
                {errors.postalCode && (
                  <p className="mt-1 text-sm text-red-600">{errors.postalCode.message}</p>
                )}
              </div>

              {/* Country */}
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Country
                </label>
                <input
                  type="text"
                  {...register('country')}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                />
                {errors.country && (
                  <p className="mt-1 text-sm text-red-600">{errors.country.message}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Business Tab */}
        {activeTab === 'business' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              {/* Website */}
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Website
                </label>
                <input
                  type="url"
                  {...register('website')}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                  placeholder="https://example.com"
                />
                {errors.website && (
                  <p className="mt-1 text-sm text-red-600">{errors.website.message}</p>
                )}
              </div>

              {/* Date of Birth (for individuals) */}
              {watchedType === CustomerType.INDIVIDUAL && (
                <div>
                  <label className="block text-sm font-medium text-slate-700">
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    {...register('dateOfBirth')}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                  />
                  {errors.dateOfBirth && (
                    <p className="mt-1 text-sm text-red-600">{errors.dateOfBirth.message}</p>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
              {/* ID Number */}
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  {watchedType === CustomerType.INDIVIDUAL ? 'ID/Passport Number' : 'Registration Number'}
                </label>
                <input
                  type="text"
                  {...register(watchedType === CustomerType.INDIVIDUAL ? 'idNumber' : 'registrationNumber')}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                />
                {(watchedType === CustomerType.INDIVIDUAL ? errors.idNumber : errors.registrationNumber) && (
                  <p className="mt-1 text-sm text-red-600">
                    {(watchedType === CustomerType.INDIVIDUAL ? errors.idNumber : errors.registrationNumber)?.message}
                  </p>
                )}
              </div>

              {/* Tax Number */}
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Tax/VAT Number
                </label>
                <input
                  type="text"
                  {...register('taxNumber')}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                />
                {errors.taxNumber && (
                  <p className="mt-1 text-sm text-red-600">{errors.taxNumber.message}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Financial Tab */}
        {activeTab === 'financial' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              {/* Credit Limit */}
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Credit Limit (R)
                </label>
                <Controller
                  name="creditLimit"
                  control={control}
                  render={({ field }) => (
                    <AmountInput
                      value={field.value}
                      onChange={field.onChange}
                      allowEmpty
                      min={0}
                      inputClassName="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    />
                  )}
                />
                {errors.creditLimit && (
                  <p className="mt-1 text-sm text-red-600">{errors.creditLimit.message}</p>
                )}
              </div>

              {/* Current Balance */}
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Current Balance (R)
                </label>
                <Controller
                  name="currentBalance"
                  control={control}
                  render={({ field }) => (
                    <AmountInput
                      value={field.value}
                      onChange={field.onChange}
                      allowEmpty
                      inputClassName="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    />
                  )}
                />
                {errors.currentBalance && (
                  <p className="mt-1 text-sm text-red-600">{errors.currentBalance.message}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Additional Tab */}
        {activeTab === 'additional' && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Description / Notes
              </label>
              <textarea
                {...register('description')}
                rows={4}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                placeholder="Additional information about the customer..."
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
              )}
            </div>
          </div>
        )}

        {/* Form Actions */}
        <TabbedFormActions
          onCancel={onCancel}
          tabCount={tabs.length}
          selectedTab={selectedTab}
          onTabChange={(index) => setActiveTab(tabs[index]?.id ?? 'basic')}
          canSubmit={canSubmit}
          submitLabel={customer ? 'Update Customer' : 'Create Customer'}
          loading={loading}
        />
      </form>
    </div>
  );
} 